import React, { useEffect, useMemo } from 'react';
import { Route, Switch, useHistory } from 'react-router';
import { Navigate } from 'react-router-dom-v5-compat';

import { GraphNav } from 'src/components/appNav/graphNav/GraphNav';
import { Loading } from 'src/components/common/loading/Loading';
import { DocumentTitle } from 'src/components/documentTitle/DocumentTitle';
import { Toasts } from 'src/components/toast/Toast';
import { useAppSettings } from 'src/hooks/useAppSettings';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { IsCloudEndpointInitiatingContextProvider } from 'src/hooks/useIsCloudEndpointInitiating';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { usePerKeyLocalStorage } from 'src/hooks/usePerKeyLocalStorage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { buildSubscriptionUrlFromEndpoint } from 'src/lib/buildSubscriptionUrlFromEndpoint';
import Config from 'src/lib/config';
import { mergeQueryParams } from 'src/lib/routing';

import { ThemeProvider } from '../../components/themeProvider/ThemeProvider';
import { GlobalGraphHeader } from '../appLayout/components/globalHeader/globalGraphHeader/GlobalGraphHeader';
import { GlobalHeader } from '../appLayout/components/globalHeader/GlobalHeader';
import { isSupportedInVersion } from '../embedHelpers/isSupportedInVersion';
import { useListenForAuthResponse } from '../embedHelpers/useListenForAuthResponse';
import { useListenForHandshakeResponseFromEmbed } from '../embedHelpers/useListenForHandshakeResponseFromEmbed';
import { usePassThroughExplorerQueryParamsFromParentPage } from '../embedHelpers/usePassThroughExplorerQueryParamsFromParentPage';
import {
  EMBEDDED_SANDBOX_EXPLORER_FLAGS,
  ExplorerBehavioralFlags,
} from '../graph/explorerPage/explorerBehavioralFlags';
import {
  CollectionEntryIdState,
  CollectionEntryState,
} from '../graph/explorerPage/hooks/useExplorerState/useEditorTabState/collectionEntryState';
import { INITIAL_HEADERS } from '../graph/explorerPage/hooks/useExplorerState/useHeadersManagerContext/shared';
import { HeadersManagerContextProvider } from '../graph/explorerPage/hooks/useExplorerState/useHeadersManagerContext/useHeadersManagerContext';
import { safeParseHeadersState } from '../graph/explorerPage/hooks/useLoadExplorerStateFromURL';
import {
  UpdateFunction,
  useLocalStorageGraphIdentifier,
  usePerGraphIdentifierLocalStorage,
} from '../graph/explorerPage/hooks/usePerGraphIdentifierLocalStorage';
import { ReachabilityProvider } from '../graph/hooks/useEndpointReachability';
import { PostMessageIntrospectionSchemaProvider } from '../graph/hooks/useSchema';
import {
  embeddableSandboxExplorerRouteConfig,
  embeddableSandboxRouteConfig,
  sandboxCheckRouteConfig,
  sandboxDiffRouteConfig,
  sandboxSchemaRouteConfig,
} from '../graph/routes';
import {
  EMBEDDED_SANDBOX_GRAPH_FLAGS,
  GraphBehavioralFlags,
} from '../graph/variantLayout/graphBehavioralFlags/graphBehavioralFlags';
import { SharedSandboxAndGraphModals } from '../graph/variantLayout/VariantLayoutModals';

import { useEmbeddableSandboxConfig } from './useEmbeddableSandboxConfig';

const ExplorerPage = React.lazy(async () => ({
  default: (
    await import(
      /* webpackChunkName: "ExplorerPage" */ '../graph/explorerPage/ExplorerPage'
    )
  ).ExplorerPage,
}));

const MIN_VERSION_WITH_FILE_UPLOAD_SUPPORT = '2.3.0';

const SandboxExplorerPage = ({
  graphBehavioralFlags,
  embeddedSandboxExplorerBehavioralFlags,
  setEmbedDefaultTabState,
}: {
  graphBehavioralFlags: GraphBehavioralFlags;
  embeddedSandboxExplorerBehavioralFlags: ExplorerBehavioralFlags;
  setEmbedDefaultTabState: UpdateFunction<
    CollectionEntryState | CollectionEntryIdState | undefined
  >;
}) => {
  const {
    defaultDocument,
    defaultHeaders,
    defaultVariables,
    defaultCollectionEntryId,
    defaultCollectionId,
  } = useRouteParams(embeddableSandboxExplorerRouteConfig);

  const history = useHistory();

  useEffect(() => {
    if (defaultCollectionEntryId && defaultCollectionId) {
      setEmbedDefaultTabState({
        collectionId: defaultCollectionId,
        collectionEntryId: defaultCollectionEntryId,
      });
    } else if (defaultDocument || defaultHeaders || defaultVariables) {
      setEmbedDefaultTabState({
        headers: defaultHeaders
          ? safeParseHeadersState(defaultHeaders)
          : undefined,
        operation: defaultDocument,
        variables: defaultVariables,
      });
    }
    history.replace({
      ...history.location,
      search: mergeQueryParams(history.location.search, {
        [Config.queryParameters.EmbeddableExplorerDefaultDocument]: undefined,
        [Config.queryParameters.EmbeddableExplorerDefaultHeaders]: undefined,
        [Config.queryParameters.EmbeddableExplorerDefaultVariables]: undefined,
        [Config.queryParameters.EmbeddableExplorerDefaultCollectionEntryId]:
          undefined,
        [Config.queryParameters.EmbeddableExplorerDefaultCollectionId]:
          undefined,
      }),
    });
  }, [
    defaultCollectionEntryId,
    defaultCollectionId,
    defaultDocument,
    defaultHeaders,
    defaultVariables,
    history,
    setEmbedDefaultTabState,
  ]);

  return (
    <ExplorerPage
      userId={undefined}
      explorerBehavioralFlags={embeddedSandboxExplorerBehavioralFlags}
      graphBehavioralFlags={graphBehavioralFlags}
    />
  );
};

const DocumentationPage = React.lazy(async () => ({
  default: (
    await import(
      /* webpackChunkName: "DocumentationPage" */ '../graph/documentationPage/DocumentationPage'
    )
  ).DocumentationPage,
}));

const SandboxDiffPage = React.lazy(async () => ({
  default: (
    await import(
      /* webpackChunkName: "SandboxDiffPage" */ '../graph/sandboxDiffPage/SandboxDiffPage'
    )
  ).SandboxDiffPage,
}));

const SandboxCheckPage = React.lazy(async () => ({
  default: (
    await import(
      /* webpackChunkName: "SandboxCheckPage" */ '../graph/sandboxCheckPage/SandboxCheckPage'
    )
  ).SandboxCheckPage,
}));

/**
 *
 * Wrapper around EmbeddableSandbox to make sure the current endpoint url
 * is written to local storage before doing any local storage mutations in
 * the endpoint url context
 */
export const EmbeddableSandbox = () => {
  const [sandboxUrl, setSandboxUrl] = useLocalStorage('sandboxUrl');
  const { endpoint } = useRouteParams(embeddableSandboxRouteConfig);

  useEffect(() => {
    if (endpoint) setSandboxUrl(endpoint);
  }, [endpoint, setSandboxUrl]);

  return !endpoint || sandboxUrl === endpoint ? (
    <EmbeddableSandboxBody />
  ) : (
    <Loading />
  );
};

const EmbeddableSandboxBody = () => {
  const { navCollapsed, setNavCollapsed } = useAppSettings();
  const [currentAccountId] = useCurrentAccountId();
  const [sandboxUrl] = useLocalStorage('sandboxUrl');

  const graphIdentifier = useLocalStorageGraphIdentifier({
    shouldNamespaceSandboxByEndpoint: false,
    shouldOnlyStoreForRegisteredGraphs: false,
  });
  const [embedDefaultTabState, setEmbedDefaultTabState] =
    usePerGraphIdentifierLocalStorage({
      graphIdentifier,
      key: 'embedDefaultTabState',
      stableInitialValue: undefined,
    });

  const config = useEmbeddableSandboxConfig();

  const graphBehavioralFlags = EMBEDDED_SANDBOX_GRAPH_FLAGS({
    shouldAutoUpdateSchemaInitialValue: config.shouldDefaultAutoupdateSchema,
    endpointIsEditable: config.endpointIsEditable,
    sendCookiesInitialValue: !config.hideCookieToggle,
    canEditSharedHeaders: !config.sharedHeaders,
    showCookieToggle: !config.hideCookieToggle,
    initialSubscriptionUrl: config.subscriptionEndpoint,
  });

  const embeddedSandboxExplorerBehavioralFlags = useMemo(
    () =>
      EMBEDDED_SANDBOX_EXPLORER_FLAGS({
        defaultTabState: embedDefaultTabState,
        initialRequestQueryPlan: config.initialRequestQueryPlan,
        initialRequestConnectorsDebugging:
          config.initialRequestConnectorsDebugging,
        initialRequestCacheDebugging: config.initialRequestCacheDebugging,
        showFileUpload: isSupportedInVersion(
          MIN_VERSION_WITH_FILE_UPLOAD_SUPPORT,
          config.version,
        ),
      }),
    [
      config.initialRequestQueryPlan,
      embedDefaultTabState,
      config.version,
      config.initialRequestConnectorsDebugging,
      config.initialRequestCacheDebugging,
    ],
  );

  const [sandboxConnectionSettings, _, setSandboxConnectionSettingsForKey] =
    usePerKeyLocalStorage({
      localStorageKey: 'sandboxConnectionSettings',
      key: sandboxUrl,
      initialValue:
        graphBehavioralFlags.sandboxConnectionSettingsInitialValue(sandboxUrl),
    });

  // NOTE: If the user cannot set their subscription url
  // and the subscription url differs from the one inputted into the config,
  // their admin updated the url and we want to update their local storage
  // with the new config values
  useEffect(() => {
    const newSubscriptionUrl =
      config.subscriptionEndpoint ??
      buildSubscriptionUrlFromEndpoint(sandboxUrl);
    if (
      !config.endpointIsEditable &&
      sandboxConnectionSettings.subscriptionUrl !== newSubscriptionUrl
    ) {
      setSandboxConnectionSettingsForKey(
        {
          ...sandboxConnectionSettings,
          subscriptionUrl: newSubscriptionUrl,
        },
        sandboxUrl,
      );
    }
  }, [
    config.endpointIsEditable,
    config.subscriptionEndpoint,
    sandboxConnectionSettings,
    sandboxUrl,
    setSandboxConnectionSettingsForKey,
  ]);

  // NOTE: If the user cannot toggle cookies and the admin has set a default
  // includeCookie value that is different from the one in local storage
  // we want to prevent the user from having to manually clear their cache
  // so we should overwrite with our calculated initial value.
  useEffect(() => {
    if (
      config.hideCookieToggle &&
      sandboxConnectionSettings.sendCookies !== config.defaultIncludeCookies
    ) {
      setSandboxConnectionSettingsForKey(
        {
          ...sandboxConnectionSettings,
          sendCookies: config.defaultIncludeCookies,
        },
        sandboxUrl,
      );
    }
  }, [
    config.defaultIncludeCookies,
    config.hideCookieToggle,
    sandboxConnectionSettings,
    sandboxUrl,
    setSandboxConnectionSettingsForKey,
  ]);

  const [hasMigratedSendCookiesFromEmbed, setHasMigratedSendCookiesFromEmbed] =
    usePerKeyLocalStorage({
      localStorageKey: 'hasMigratedSendCookiesFromEmbed',
      key: sandboxUrl,
      initialValue: false,
    });

  // We use the query param here instead of the defaulted config b/c we
  // only act if the admin passed a value, not if its undefined.
  const { defaultIncludeCookies: defaultIncludeCookiesFromParams } =
    useRouteParams(embeddableSandboxRouteConfig);
  // NOTE: If the user can toggle cookies and the admin has set a default
  // includeCookie value we should do a one-time migration of the user's
  // existing local storage value over to the one set by the admin. This exists
  // for situations where the graph admin changes the settings and
  // wants to update the includeCookies value for everyone.
  useEffect(() => {
    if (
      !config.hideCookieToggle &&
      typeof defaultIncludeCookiesFromParams === 'string' &&
      !hasMigratedSendCookiesFromEmbed
    ) {
      setSandboxConnectionSettingsForKey(
        {
          ...sandboxConnectionSettings,
          sendCookies: defaultIncludeCookiesFromParams === 'true',
        },
        sandboxUrl,
      );
      setHasMigratedSendCookiesFromEmbed(true);
    }
  }, [
    config.hideCookieToggle,
    defaultIncludeCookiesFromParams,
    hasMigratedSendCookiesFromEmbed,
    sandboxConnectionSettings,
    sandboxUrl,
    setHasMigratedSendCookiesFromEmbed,
    setSandboxConnectionSettingsForKey,
  ]);

  // we store the embedAuthenticationDetails by document.referrer
  const [embedAuthenticationDetails] = usePerKeyLocalStorage({
    initialValue: undefined,
    key: document.referrer,
    localStorageKey: 'embedAuthenticationDetails',
  });

  const [__, setLocalSharedHeaderDefinitions] =
    usePerGraphIdentifierLocalStorage({
      key: 'localDefaultHeaders',
      graphIdentifier,
      stableInitialValue: INITIAL_HEADERS,
    });

  useEffect(() => {
    if (config.sharedHeaders) {
      setLocalSharedHeaderDefinitions(config.sharedHeaders);
    }
  }, [setLocalSharedHeaderDefinitions, config.sharedHeaders]);

  useListenForHandshakeResponseFromEmbed();
  useListenForAuthResponse();
  usePassThroughExplorerQueryParamsFromParentPage();

  if (!embedAuthenticationDetails?.origin) {
    return <Loading />;
  }

  return (
    <div className="flex h-full flex-col">
      <div className="relative flex flex-1 flex-col overflow-y-auto overflow-x-hidden text-primary">
        <HeadersManagerContextProvider graphRef={null}>
          <IsCloudEndpointInitiatingContextProvider graphRef={null}>
            <ReachabilityProvider>
              <PostMessageIntrospectionSchemaProvider
                sandboxConnectionSettings={sandboxConnectionSettings}
              >
                <GlobalHeader
                  leftNavContext={navCollapsed ? 'collapsed' : 'expanded'}
                >
                  <GlobalGraphHeader
                    key="Embedded-Sandbox"
                    graphBehavioralFlags={graphBehavioralFlags}
                  />
                </GlobalHeader>
                <div className="flex h-full flex-1 overflow-y-auto">
                  <Toasts />
                  <SharedSandboxAndGraphModals
                    currentAccountId={currentAccountId}
                    explorerBehavioralFlags={
                      embeddedSandboxExplorerBehavioralFlags
                    }
                    graphBehavioralFlags={graphBehavioralFlags}
                  />
                  <GraphNav
                    collapsed={navCollapsed}
                    setCollapsed={setNavCollapsed}
                  />
                  <div className="relative flex flex-1 flex-col overflow-x-hidden overscroll-y-auto">
                    <DocumentTitle title="Sandbox">
                      <Switch>
                        <Route
                          path={embeddableSandboxExplorerRouteConfig.definition}
                          render={() => {
                            return (
                              <>
                                <DocumentTitle title="Explorer" />
                                <ThemeProvider className="size-full">
                                  <SandboxExplorerPage
                                    setEmbedDefaultTabState={
                                      setEmbedDefaultTabState
                                    }
                                    embeddedSandboxExplorerBehavioralFlags={
                                      embeddedSandboxExplorerBehavioralFlags
                                    }
                                    graphBehavioralFlags={graphBehavioralFlags}
                                  />
                                </ThemeProvider>
                              </>
                            );
                          }}
                        />
                        <Route
                          path={sandboxDiffRouteConfig.definition}
                          render={() => {
                            return (
                              <>
                                <DocumentTitle title="Schema Diff" />
                                <SandboxDiffPage />
                              </>
                            );
                          }}
                        />
                        <Route
                          path={sandboxCheckRouteConfig.definition}
                          render={() => {
                            return (
                              <>
                                <DocumentTitle title="Checks" />
                                <SandboxCheckPage />
                              </>
                            );
                          }}
                        />
                        <Route
                          path={sandboxSchemaRouteConfig.definition}
                          render={() => (
                            <>
                              <DocumentTitle title="Schema" />
                              <DocumentationPage />
                            </>
                          )}
                        />
                        <Route
                          render={() => (
                            <Navigate
                              replace={true}
                              to={embeddableSandboxExplorerRouteConfig.path({})}
                            />
                          )}
                        />
                      </Switch>
                    </DocumentTitle>
                  </div>
                </div>
              </PostMessageIntrospectionSchemaProvider>
            </ReachabilityProvider>
          </IsCloudEndpointInitiatingContextProvider>
        </HeadersManagerContextProvider>
      </div>
    </div>
  );
};
